import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DialogFooter } from '@/components/ui/dialog';
import { useCreateLegalFeeStages } from '@/hooks/useLegalFeeStages';
import { STAGE_TEMPLATE } from './legalFees.utils';

// ---------------------------------------------------------------------------
// Local types for editable stage/item state
// ---------------------------------------------------------------------------

interface EditableItem {
  id: string; // local-only key for React list rendering
  title: string;
}

interface EditableStage {
  id: string; // local-only key for React list rendering
  name: string;
  items: EditableItem[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

let _uid = 0;
function uid() {
  return String(++_uid);
}

/** Build the initial editable state from STAGE_TEMPLATE. */
function buildInitialStages(): EditableStage[] {
  return STAGE_TEMPLATE.map((tpl) => ({
    id: uid(),
    name: tpl.name,
    items: tpl.items.map((title: string) => ({ id: uid(), title })),
  }));
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface InitLegalFeesFormProps {
  fixedAssetId: string;
  onSuccess: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function InitLegalFeesForm({ fixedAssetId, onSuccess }: InitLegalFeesFormProps) {
  const [stages, setStages] = useState<EditableStage[]>(buildInitialStages);
  const [newStageName, setNewStageName] = useState('');
  const createStages = useCreateLegalFeeStages();

  // ── Stage mutations ──────────────────────────────────────────────────────

  function handleRemoveStage(stageId: string) {
    setStages((prev) => prev.filter((s) => s.id !== stageId));
  }

  function handleAddStage() {
    const name = newStageName.trim();
    if (!name) return;
    setStages((prev) => [
      ...prev,
      { id: uid(), name, items: [] },
    ]);
    setNewStageName('');
  }

  function handleStageName(stageId: string, value: string) {
    setStages((prev) =>
      prev.map((s) => (s.id === stageId ? { ...s, name: value } : s)),
    );
  }

  // ── Item mutations ───────────────────────────────────────────────────────

  function handleRemoveItem(stageId: string, itemId: string) {
    setStages((prev) =>
      prev.map((s) =>
        s.id === stageId
          ? { ...s, items: s.items.filter((i) => i.id !== itemId) }
          : s,
      ),
    );
  }

  function handleAddItem(stageId: string) {
    setStages((prev) =>
      prev.map((s) =>
        s.id === stageId
          ? { ...s, items: [...s.items, { id: uid(), title: '' }] }
          : s,
      ),
    );
  }

  function handleItemTitle(stageId: string, itemId: string, value: string) {
    setStages((prev) =>
      prev.map((s) =>
        s.id === stageId
          ? {
              ...s,
              items: s.items.map((i) => (i.id === itemId ? { ...i, title: value } : i)),
            }
          : s,
      ),
    );
  }

  // ── Submit ───────────────────────────────────────────────────────────────

  async function handleConfirm() {
    // Build JSONB payload — filter out stages with no name and items with no title
    const payload = stages
      .filter((s) => s.name.trim())
      .map((s, si) => ({
        name: s.name.trim(),
        sort_order: si,
        items: s.items
          .filter((i) => i.title.trim())
          .map((i, ii) => ({
            title: i.title.trim(),
            currency: 'USD',
            amount: 0,
            description: null,
            sort_order: ii,
          })),
      }));

    try {
      await createStages.mutateAsync({ fixedAssetId, stages: payload });
      toast.success('Legal fee tracking set up successfully');
      onSuccess();
    } catch (error: any) {
      toast.error(error?.message ?? 'Failed to set up legal fee tracking');
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Review the suggested stages below. Add, remove, or rename stages and items before
        confirming.
      </p>

      {/* Stage list */}
      <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-1">
        {stages.map((stage) => (
          <div
            key={stage.id}
            className="rounded-md border p-3 space-y-3"
            data-testid="stage-block"
          >
            {/* Stage header */}
            <div className="flex items-center gap-2">
              <div className="flex-1 space-y-1">
                <Label htmlFor={`stage-name-${stage.id}`} className="sr-only">
                  Stage name
                </Label>
                <Input
                  id={`stage-name-${stage.id}`}
                  value={stage.name}
                  onChange={(e) => handleStageName(stage.id, e.target.value)}
                  placeholder="Stage name"
                  className="font-medium"
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => handleRemoveStage(stage.id)}
                aria-label={`Remove stage ${stage.name}`}
                data-testid="remove-stage"
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>

            {/* Items */}
            <div className="space-y-2 pl-2">
              {stage.items.map((item) => (
                <div key={item.id} className="flex items-center gap-2">
                  <Input
                    value={item.title}
                    onChange={(e) => handleItemTitle(stage.id, item.id, e.target.value)}
                    placeholder="Item title"
                    className="flex-1 text-sm"
                    aria-label="Item title"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveItem(stage.id, item.id)}
                    aria-label={`Remove item ${item.title}`}
                    data-testid="remove-item"
                  >
                    <Trash2 className="h-3 w-3 text-muted-foreground" />
                  </Button>
                </div>
              ))}

              {/* Add item button */}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-xs text-muted-foreground h-7"
                onClick={() => handleAddItem(stage.id)}
                data-testid="add-item"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add item
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Add new stage */}
      <div className="flex items-center gap-2">
        <Input
          value={newStageName}
          onChange={(e) => setNewStageName(e.target.value)}
          placeholder="New stage name…"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAddStage();
            }
          }}
          data-testid="new-stage-input"
        />
        <Button
          type="button"
          variant="outline"
          onClick={handleAddStage}
          disabled={!newStageName.trim()}
          data-testid="add-stage"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Stage
        </Button>
      </div>

      <DialogFooter>
        <Button
          type="button"
          onClick={handleConfirm}
          disabled={createStages.isPending}
        >
          {createStages.isPending ? 'Setting up…' : 'Confirm'}
        </Button>
      </DialogFooter>
    </div>
  );
}
